import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Building, CreditCard, Shield, Scale, Mail } from 'lucide-react';

const CGVPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1a] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-400 hover:text-emerald-400 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Conditions Générales de Vente (CGV)</h1>
              <p className="text-gray-400">VALORAMAP</p>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm">Dernière mise à jour : 31 janvier 2026</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-emerald max-w-none">
          {/* Section 1 */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-emerald-400" />
              1. Préambule
            </h2>
            <p className="text-gray-300 mb-4">
              Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre :
            </p>
            <div className="bg-black/30 rounded-lg p-4 mb-4">
              <p className="text-white font-medium">Scalabla Swiss Sàrl</p>
              <p className="text-gray-400">Rue de la Promenade-Noire 5</p>
              <p className="text-gray-400">c/o Fiduciaire Daniel Jaggi S.A.</p>
              <p className="text-gray-400">2000 Neuchâtel, Suisse</p>
              <p className="text-emerald-400 mt-2">IDE : CHE-411.341.565</p>
            </div>
            <p className="text-gray-300">
              Ci-après dénommée "le Prestataire" et toute personne physique ou morale souscrivant un abonnement 
              au service VALORAMAP, ci-après dénommée "le Client".
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">2. Objet</h2>
            <p className="text-gray-300">
              Les présentes CGV définissent les conditions de souscription, de paiement et d'utilisation des 
              abonnements au service VALORAMAP.
            </p>
            <p className="text-gray-300 mt-2">
              <strong className="text-white">Toute souscription implique l'acceptation sans réserve des présentes CGV.</strong>
            </p>
          </section>

          {/* Section 3 - Tarifs */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              3. Offres et Tarifs
            </h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">3.1 Formules d'abonnement</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Formule</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Prix</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Durée</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Particularités</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-4 text-white">Essai</td>
                    <td className="py-3 px-4 text-emerald-400">9 CHF</td>
                    <td className="py-3 px-4 text-gray-300">7 jours</td>
                    <td className="py-3 px-4 text-gray-300">Accès complet, déduit du premier abonnement</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-4 text-white">Founding</td>
                    <td className="py-3 px-4 text-emerald-400">279 CHF</td>
                    <td className="py-3 px-4 text-gray-300">12 mois</td>
                    <td className="py-3 px-4 text-gray-300">Limité à 50 places, prix garanti à vie</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-white">Premium</td>
                    <td className="py-3 px-4 text-emerald-400">399 CHF</td>
                    <td className="py-3 px-4 text-gray-300">12 mois</td>
                    <td className="py-3 px-4 text-gray-300">Formule standard</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">3.2 Devise</h3>
            <p className="text-gray-300">
              Tous les prix sont indiqués en francs suisses (CHF), toutes taxes comprises. 
              Scalabla Swiss Sàrl n'est pas assujettie à la TVA.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">3.3 Modification des tarifs</h3>
            <p className="text-gray-300">
              Le Prestataire se réserve le droit de modifier ses tarifs à tout moment. Les nouveaux tarifs 
              s'appliquent aux nouvelles souscriptions et aux renouvellements. 
              <strong className="text-emerald-400"> Les abonnements "Founding" bénéficient d'un prix garanti à vie.</strong>
            </p>
          </section>

          {/* Section 4 - Souscription */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">4. Souscription</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">4.1 Processus de souscription</h3>
            <p className="text-gray-300 mb-3">La souscription s'effectue en ligne sur le site du Service :</p>
            <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
              <li>Création d'un compte utilisateur</li>
              <li>Choix de la formule d'abonnement</li>
              <li>Saisie des informations de paiement</li>
              <li>Validation et paiement</li>
              <li>Activation immédiate du Service</li>
            </ol>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">4.2 Période d'essai</h3>
            <p className="text-gray-300 mb-3">
              La période d'essai de 7 jours à 9 CHF donne accès à l'intégralité des fonctionnalités du Service.
            </p>
            <p className="text-gray-300 mb-2">À l'issue de la période d'essai :</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Si le Client souscrit un abonnement annuel, les 9 CHF sont déduits du montant de l'abonnement</li>
              <li>Si le Client ne souscrit pas et annule avant le 7ème jour, les 9 CHF sont intégralement remboursés</li>
              <li>Si le Client ne fait rien, aucun prélèvement automatique n'est effectué et l'accès prend fin</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">4.3 Formule Founding</h3>
            <p className="text-gray-300">
              La formule Founding est limitée à 50 places. Une fois ce quota atteint, cette formule n'est plus disponible. 
              Les membres Founding conservent leur tarif préférentiel de 279 CHF/an à vie, même en cas d'augmentation du tarif standard.
            </p>
          </section>

          {/* Section 5 - Paiement */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">5. Paiement</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">5.1 Moyens de paiement</h3>
            <p className="text-gray-300">
              Le paiement s'effectue par carte bancaire (Visa, Mastercard, American Express) via la plateforme sécurisée Stripe.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">5.2 Paiement fractionné</h3>
            <p className="text-gray-300 mb-3">Le Client peut opter pour un paiement en 4 fois sans frais :</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Founding : 4 × 69,75 CHF</li>
              <li>Premium : 4 × 99,75 CHF</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">5.3 Facturation</h3>
            <p className="text-gray-300">
              Une facture est émise et envoyée par email à chaque paiement. Les factures sont également accessibles depuis l'espace client.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">5.4 Défaut de paiement</h3>
            <p className="text-gray-300">
              En cas d'échec de paiement, le Prestataire informera le Client par email. À défaut de régularisation sous 7 jours, l'accès au Service pourra être suspendu.
            </p>
          </section>

          {/* Section 6 - Rétractation */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              6. Droit de Rétractation et Remboursement
            </h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">6.1 Période d'essai (9 CHF)</h3>
            <p className="text-gray-300">
              Le Client peut annuler sa période d'essai à tout moment avant le 7ème jour et obtenir un remboursement intégral des 9 CHF.
              Pour annuler : depuis les paramètres du compte ou en contactant le Prestataire.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">6.2 Abonnement annuel (279 CHF / 399 CHF)</h3>
            <p className="text-gray-300">
              Le Client dispose d'un délai de <strong className="text-white">14 jours</strong> à compter de la souscription pour annuler 
              son abonnement annuel et obtenir un remboursement intégral.
            </p>
            <p className="text-amber-400 mt-2">
              Passé ce délai de 14 jours, aucun remboursement ne sera accordé, même en cas de résiliation anticipée.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">6.3 Procédure de remboursement</h3>
            <p className="text-gray-300">
              Les remboursements sont effectués sur le moyen de paiement utilisé lors de la souscription, 
              dans un délai de 14 jours ouvrés suivant la demande.
            </p>
          </section>

          {/* Section 7 - Durée */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">7. Durée et Renouvellement</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">7.1 Durée</h3>
            <p className="text-gray-300">
              Les abonnements sont souscrits pour une durée de 12 mois à compter de la date de souscription.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">7.2 Renouvellement</h3>
            <p className="text-gray-300">
              Les abonnements sont renouvelés automatiquement à leur date d'anniversaire, sauf résiliation par le Client.
              Un email de rappel est envoyé 14 jours avant la date de renouvellement.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">7.3 Résiliation</h3>
            <p className="text-gray-300">
              Le Client peut résilier son abonnement à tout moment depuis les paramètres de son compte. 
              La résiliation prend effet à la fin de la période d'abonnement en cours. 
              Aucun remboursement au prorata n'est accordé.
            </p>
          </section>

          {/* Section 8 - Obligations Prestataire */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">8. Obligations du Prestataire</h2>
            <p className="text-gray-300 mb-3">Le Prestataire s'engage à :</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Fournir un accès au Service conformément aux formules souscrites</li>
              <li>Assurer la disponibilité du Service dans la mesure du possible</li>
              <li>Informer le Client des maintenances programmées</li>
              <li>Traiter les données personnelles conformément à la réglementation applicable</li>
              <li>Répondre aux demandes du Client dans un délai raisonnable</li>
            </ul>
          </section>

          {/* Section 9 - Obligations Client */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">9. Obligations du Client</h2>
            <p className="text-gray-300 mb-3">Le Client s'engage à :</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Fournir des informations exactes lors de la souscription</li>
              <li>Régler les sommes dues aux échéances prévues</li>
              <li>Utiliser le Service conformément aux CGU</li>
              <li>Ne pas partager ses identifiants de connexion</li>
              <li>Informer le Prestataire de tout changement de ses coordonnées</li>
            </ul>
          </section>

          {/* Section 10 - Limitation responsabilité */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-emerald-400" />
              10. Limitation de Responsabilité
            </h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">10.1 Exclusion de conseil en investissement</h3>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
              <p className="text-amber-200">
                Le Service fournit des informations financières à titre informatif. 
                <strong> Il ne constitue pas un conseil en investissement au sens de la LSFIN</strong> (Loi sur les services financiers).
              </p>
              <p className="text-amber-200 mt-2">
                Le Prestataire n'est pas un établissement financier soumis à surveillance et ne dispose pas d'autorisation de la FINMA.
              </p>
            </div>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">10.2 Responsabilité financière</h3>
            <p className="text-gray-300">
              Le Prestataire décline toute responsabilité quant aux décisions d'investissement prises par le Client 
              sur la base des informations fournies par le Service.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">10.3 Plafond de responsabilité</h3>
            <p className="text-gray-300">
              La responsabilité totale du Prestataire est limitée au montant des sommes effectivement versées par le Client 
              au cours des 12 mois précédant le fait générateur.
            </p>
          </section>

          {/* Section 11 - Force Majeure */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">11. Force Majeure</h2>
            <p className="text-gray-300">
              Le Prestataire ne saurait être tenu responsable de l'inexécution de ses obligations en cas de force majeure, 
              notamment : catastrophe naturelle, guerre, grève, panne généralisée d'internet, décision gouvernementale.
            </p>
          </section>

          {/* Section 12 - Modification CGV */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">12. Modification des CGV</h2>
            <p className="text-gray-300">
              Le Prestataire se réserve le droit de modifier les présentes CGV. Les modifications seront notifiées par email 
              au moins 30 jours avant leur entrée en vigueur. Le Client qui n'accepte pas les nouvelles CGV peut résilier 
              son abonnement avant leur entrée en vigueur.
            </p>
          </section>

          {/* Section 13 - Droit applicable */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">13. Droit Applicable et Litiges</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">13.1 Droit applicable</h3>
            <p className="text-gray-300">Les présentes CGV sont soumises au droit suisse.</p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">13.2 Règlement des litiges</h3>
            <p className="text-gray-300">
              En cas de litige, le Client est invité à contacter le Prestataire pour rechercher une solution amiable.
              À défaut de résolution amiable dans un délai de 30 jours, le litige sera soumis aux tribunaux compétents 
              du canton de Neuchâtel.
            </p>
          </section>

          {/* Section 14 - Dispositions diverses */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">14. Dispositions Diverses</h2>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-3">14.1 Intégralité</h3>
            <p className="text-gray-300">
              Les présentes CGV, conjointement avec les CGU, constituent l'intégralité de l'accord entre les parties.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">14.2 Nullité partielle</h3>
            <p className="text-gray-300">
              Si une disposition des présentes CGV était déclarée nulle, les autres dispositions resteraient en vigueur.
            </p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">14.3 Tolérance</h3>
            <p className="text-gray-300">
              Le fait pour le Prestataire de ne pas exercer un droit prévu aux présentes CGV ne constitue pas une renonciation à ce droit.
            </p>
          </section>

          {/* Section 15 - Contact */}
          <section className="mb-10 bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-400" />
              15. Contact
            </h2>
            <p className="text-gray-300 mb-4">
              Pour toute question relative aux présentes CGV ou pour toute demande de remboursement :
            </p>
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-white font-medium">Scalabla Swiss Sàrl</p>
              <p className="text-gray-400">Rue de la Promenade-Noire 5</p>
              <p className="text-gray-400">c/o Fiduciaire Daniel Jaggi S.A.</p>
              <p className="text-gray-400">2000 Neuchâtel, Suisse</p>
            </div>
          </section>

          {/* Footer Notice */}
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-center">
            <p className="text-emerald-200">
              En souscrivant à VALORAMAP, vous reconnaissez avoir lu, compris et accepté 
              les présentes Conditions Générales de Vente.
            </p>
          </div>

          {/* Copyright */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">© 2026 Scalabla Swiss Sàrl. Tous droits réservés.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CGVPage;
